import Cookies from 'js-cookie';
import { defineStore } from 'pinia';
import { MessagePlugin } from 'tdesign-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import * as api from '@/api/auth';
import * as profileApi from '@/api/profile';
import { usePermissionStore } from '@/store/modules/permission';
import type { TokenResponse } from '@/types/business/auth';
import type { UserInfo } from '@/types/common/ui';
import * as auth from '@/utils/auth';

/**
 * 用户信息
 */
export const useUserStore = defineStore(
  'user',
  () => {
    const router = useRouter();
    /**
     * 默认令牌
     */
    const token = ref<string>();

    /**
     * 用户信息
     */
    const userInfo = ref<UserInfo | null>(null);

    /**
     * 用户权限
     */
    const permissions = computed(() => userInfo?.value?.permissions ?? []);

    /**
     * 设置用户令牌
     *
     * @param tokens 要设置的令牌（access 与 refresh）
     */
    function setToken(tokens: TokenResponse) {
      token.value = tokens.accessToken;
      auth.setToken(tokens.accessToken);
      auth.setRefreshToken(tokens.refreshToken);
    }

    /**
     * 设置用户信息
     *
     * @param info 要设置的用户信息
     */
    function setUserInfo(info: UserInfo | null) {
      userInfo.value = info;
    }

    /**
     * 退出登录
     */
    async function logout() {
      const permissionStore = usePermissionStore();
      resetProfile();
      return api.logout().then(() => {
        setUserInfo(null);
        permissionStore.resetRoutes();
        auth.removeToken();
        auth.removeRefreshToken();
      });
    }

    async function getUserProfile() {
      if (!auth.getToken()) {
        return;
      }
      try {
        const res = await profileApi.getUserProfile();
        if (res.data.code === 200) {
          setUserInfo(res.data.data);
        }
      } catch (error) {
        console.error('获取用户资料失败', error);
      }
    }

    const resetProfile = () => {
      setUserInfo(null);
    };

    async function getVersion() {
      const res = await fetch(`/version.json?_t=${Date.now()}`, { cache: 'no-store' });
      const data = await res.json();
      return data.version;
    }

    async function setVersion(version: string) {
      localStorage.setItem('app_version', version);
    }

    async function checkAppVersion() {
      try {
        const remoteVersion = await getVersion();
        const localVersion = localStorage.getItem('app_version');

        if (!localVersion) {
          setVersion(remoteVersion);
        }
        if (remoteVersion !== localVersion) {
          if (remoteVersion !== localVersion) {
            // 清除 localStorage
            localStorage.clear();
            // 清除 sessionStorage
            sessionStorage.clear();
            // 清除所有 cookie
            Object.keys(Cookies.get()).forEach((cookieName) => {
              Cookies.remove(cookieName);
            });
            MessagePlugin.error('检测到版本更新，请重新登录');
            logout();
            router.push('/login');
          }
        }
      } catch (e) {
        console.error('版本检测失败', e);
      }
    }

    return {
      token,
      userInfo,
      permissions,
      setToken,
      setUserInfo,
      logout,
      checkAppVersion,
      getVersion,
      setVersion,
      getUserProfile,
      resetProfile,
    };
  },
  {
    persist: {
      key: 'user',
    },
  },
);
